import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BellOutlined, CheckOutlined, CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import api from '../../services/api';

/* ============================================================
   确认反馈中心（AI 指挥办公室级）
   数字人团队有问题要问用户时，在指挥办公室弹提醒：
   「技术研发公司有问题需要向您确认」——用户在这里看问题、
   输入意见、点「确认反馈」，等价于数字员工页里的用户确认。
   研发办公室页面只提醒不办理，办理入口收敛在这里。
   ============================================================ */

interface PendingItem {
  key: string;
  projectId: string;
  projectName: string;
  confId: string;
  type?: string;
  title?: string;
  content: string;
  pmName?: string;
  createdAt?: string;
  accessUrls?: Array<{ label?: string; url: string }>;
}

const POLL_MS = 30000;

const normalizeUrls = (conf: any): Array<{ label?: string; url: string }> => {
  const raw = conf?.accessUrls || conf?.optionsJson?.accessUrls || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((u: any) => (typeof u === 'string' ? { url: u } : { label: u?.label, url: u?.url }))
    .filter((u: any) => typeof u.url === 'string' && /^https?:\/\//.test(u.url));
};

const ConfirmCenter: React.FC = () => {
  const [items, setItems] = useState<PendingItem[]>([]);
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [doneIds, setDoneIds] = useState<Set<string>>(new Set());
  const [err, setErr] = useState('');
  const projectsRef = useRef<Array<{ id: string; name: string }>>([]);
  const projTsRef = useRef(0);

  const pull = useCallback(async () => {
    try {
      /* 项目列表 90s 缓存一次 */
      if (Date.now() - projTsRef.current > 90000) {
        const { data } = await api.get('/digital-employees/workspace-projects');
        const list = Array.isArray(data) ? data : data?.items || [];
        projectsRef.current = list.map((p: any) => ({ id: p.id, name: p.name }));
        projTsRef.current = Date.now();
      }
      const found: PendingItem[] = [];
      await Promise.all(projectsRef.current.map(async (p) => {
        try {
          const { data } = await api.get(`/digital-employees/projects/${p.id}/pending-confirmations`);
          const list = Array.isArray(data) ? data : data?.items || [];
          for (const c of list) {
            if (!c?.id) continue;
            found.push({
              key: `${p.id}:${c.id}`,
              projectId: p.id,
              projectName: p.name,
              confId: c.id,
              type: c.type,
              title: c.title,
              content: c.content || c.question || '',
              pmName: c.pm?.name || c.pmEmployee?.name,
              createdAt: c.createdAt,
              accessUrls: normalizeUrls(c),
            });
          }
        } catch { /* 单项目失败不影响其他 */ }
      }));
      found.sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')));
      setItems(found);
      setDoneIds((prev) => {
        const next = new Set<string>();
        for (const it of found) if (prev.has(it.key)) next.add(it.key);
        return next;
      });
    } catch { /* 下轮再试 */ }
  }, []);

  useEffect(() => {
    pull();
    const t = setInterval(pull, POLL_MS);
    return () => clearInterval(t);
  }, [pull]);

  const liveItems = useMemo(() => items.filter((it) => !doneIds.has(it.key)), [items, doneIds]);

  const submit = useCallback(async (it: PendingItem) => {
    const answer = (answers[it.key] || '').trim() || '确认，同意继续。';
    setBusyId(it.key);
    setErr('');
    try {
      await api.post(`/digital-employees/confirmations/${it.confId}/answer`, { answer });
      setDoneIds((prev) => new Set(prev).add(it.key));
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || '确认反馈提交失败，请稍后再试');
    } finally {
      setBusyId(null);
    }
  }, [answers]);

  if (!liveItems.length && !open) return null;

  return (
    <>
      {/* 右下角 · 重要提醒专区（干净醒目，常驻脉动） */}
      {liveItems.length > 0 && !open && (
        <div className="aco18-dock" onClick={(e) => e.stopPropagation()}>
          <div className="cap"><i className="bar" />重要提醒</div>
          <button className="notice" onClick={() => setOpen(true)}>
            <span className="glyph">
              <i className="halo" />
              <BellOutlined />
            </span>
            <span className="tx">
              <b>技术研发公司有 {liveItems.length} 个问题需要您确认</b>
              <em>数字人团队在等您拿主意 · 点击处理</em>
            </span>
            <span className="go">›</span>
          </button>
        </div>
      )}

      {/* 确认反馈面板 */}
      {open && (
        <div className="aco18-wrap" onClick={() => setOpen(false)}>
          <div className="aco18-panel" onClick={(e) => e.stopPropagation()}>
            <header>
              <BellOutlined className="ic" />
              <div className="tt">
                <b>技术研发公司 · 确认反馈</b>
                <span>数字人团队在等您拿主意；可直接确认，也可以输入您的意见</span>
              </div>
              <button className="x" onClick={() => setOpen(false)}><CloseOutlined /></button>
            </header>
            {err && <div className="err">{err}</div>}
            <div className="list">
              {liveItems.length === 0 && (
                <div className="allok"><CheckOutlined /> 所有事项都已反馈完毕，团队继续开工</div>
              )}
              {liveItems.map((it) => (
                <div key={it.key} className="card">
                  <div className="ch">
                    <i className="tag">{it.projectName}</i>
                    {it.pmName && <em>{it.pmName} 提问</em>}
                    {it.title && <b>{it.title}</b>}
                  </div>
                  <div className="body">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{it.content}</ReactMarkdown>
                  </div>
                  {!!it.accessUrls?.length && (
                    <div className="urls">
                      {it.accessUrls.map((u, i) => (
                        <a key={i} href={u.url} target="_blank" rel="noreferrer">{u.label || u.url}</a>
                      ))}
                    </div>
                  )}
                  <div className="reply">
                    <textarea
                      rows={2}
                      placeholder="输入您的意见（留空则默认答复：确认，同意继续。）"
                      value={answers[it.key] || ''}
                      onChange={(e) => setAnswers((prev) => ({ ...prev, [it.key]: e.target.value }))}
                    />
                    <button className="ok" disabled={busyId === it.key} onClick={() => submit(it)}>
                      {busyId === it.key ? <LoadingOutlined /> : <CheckOutlined />} 确认反馈
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConfirmCenter;
